/* eslint-disable operator-linebreak */
import _ from 'underscore';
import imageRepository from '@bingads-webui-campaign-legacy/adextension-shared/service/image-repository';

import { constants, ImageExperienceImage } from '@bingads-webui-campaign-react/image-experience';

const imageUrlPidString = 'pid=21.2';
const imageUrlAdsNativePidString = 'pid=AdsNative';

const searchAspectRatios = [
  'Image178x100',
  'Image133x100',
];

const stripOffCropParamsFromImageUrl = (url) => {
  let newUrl;

  if (url.includes(imageUrlAdsNativePidString)) {
    newUrl = new URL(url.split(imageUrlAdsNativePidString)[0]);
    newUrl.searchParams.append('pid', 'AdsNative');
  } else {
    newUrl = new URL(url.split(imageUrlPidString)[0]);
    newUrl.searchParams.append('pid', 21.2);
  }

  return newUrl.toString();
};

class DataModelConverter {
  getImagesFromMTImages(mtImages) {
    return mtImages.map((mtImage) => {
      return {
        AssetType: mtImage.Asset.SubType,
        Id: mtImage.Asset.Id,
        Url: mtImage.Asset.ImageUrl,
        ThumbnailUrl: `${mtImage.Asset.ImageUrl}&w=100`,
      };
    });
  }

  getImagesFromImageList(imageList, isImageFittingEnabled = false) {
    const oDataObjs = _.map(imageList, (imageObj) => {
      let croppingType = imageObj.cropSettings.CroppingType;

      // For audience flow, set CroppingType to undefined to let supply side to handle it
      if (isImageFittingEnabled && !_.contains(searchAspectRatios, imageObj.type)
        && imageObj.cropSettings.CroppingType !== constants.CroppingType.ManualCrop
        && imageObj.cropSettings.CroppingType !== constants.CroppingType.CenterCrop) {
        croppingType = undefined;
      }

      return {
        Id: '',
        Asset: {
          '@odata.type': '#Model.ImageAsset',
          Id: imageObj.id,
          IdString: imageObj.IdString,
          SubType: constants.aspectRatios[imageObj.type].associationType,
          CropX: imageObj.cropSettings.SourceX,
          CropY: imageObj.cropSettings.SourceY,
          CropHeight: imageObj.cropSettings.SourceHeight,
          CropWidth: imageObj.cropSettings.SourceWidth,
          CroppingType: croppingType || undefined,
        },
      };
    });

    return oDataObjs;
  }

  convetMtImageToImageExperienceModel(mtImage) {
    const imageExperienceModelSettings = {
      id: mtImage.Asset.Id,
      idString: mtImage.Asset.IdString,
      width: mtImage.Asset.Width,
      height: mtImage.Asset.Height,
    };

    // Legacy unsupported 12x10 image association.
    if (mtImage.Asset.Type === 'Image12x10') {
      // update to a wider image
      const sourceY = Math.floor((mtImage.Asset.Height - (100 * mtImage.Asset.Width / 133)) / 2);

      _.extend(
        imageExperienceModelSettings,
        {
          type: 'Image133x100',
          src: stripOffCropParamsFromImageUrl(mtImage.Asset.Url),
          cropSettings: {
            SourceX: 0,
            SourceY: sourceY,
            SourceHeight: mtImage.Asset.Height,
            SourceWidth: mtImage.Asset.Width,
          },
          associationType: mtImage.Asset.SubType,
        },
      );
    } else {
      _.extend(
        imageExperienceModelSettings,
        {
          type: constants.subTypeToType[mtImage.Asset.SubType],
          src: stripOffCropParamsFromImageUrl(mtImage.Asset.ImageUrl),
          cropSettings: {
            SourceX: mtImage.Asset.CropX,
            SourceY: mtImage.Asset.CropY,
            SourceHeight: mtImage.Asset.CropHeight,
            SourceWidth: mtImage.Asset.CropWidth,
            CroppingType: mtImage.Asset.CroppingType || undefined,
          },
          associationType: mtImage.Asset.SubType,
        },
      );
    }

    return imageExperienceModelSettings;
  }

  getImageListFromMTImagesAsync(mtImageArr, isImageFittingEnabled = false) {
    const imageIdsToFetch = _.unique(_.map(mtImageArr, (mtImageObj) => {
      return mtImageObj.Asset.IdString;
    }));

    return imageRepository.getImagesByIdsAsync(imageIdsToFetch, isImageFittingEnabled).then((returnedImages) => {
      const imageIdToMetaDataMap = _.reduce(returnedImages, (memo, returnedImage) => {
        const newData = {};

        newData[returnedImage.Id] = returnedImage;
        return _.defaults(newData, memo);
      }, {});

      return _.map(mtImageArr, (mtImage) => {
        const mtImageModel = imageIdToMetaDataMap[mtImage.Asset.Id];
        const hasBeenBulkCropped = mtImageModel.OriginalUrl &&
          mtImageModel.OriginalUrl !== mtImageModel.Url;

        const imageExperienceModelSettings = this.convetMtImageToImageExperienceModel(mtImage);

        imageExperienceModelSettings.width = imageExperienceModelSettings.width ? imageExperienceModelSettings.width : mtImageModel.Width;
        imageExperienceModelSettings.height = imageExperienceModelSettings.height ? imageExperienceModelSettings.height : mtImageModel.Height;

        if (hasBeenBulkCropped) {
          imageExperienceModelSettings.src = mtImageModel.OriginalUrl;
          imageExperienceModelSettings.height = mtImageModel.OriginalHeight;
          imageExperienceModelSettings.width = mtImageModel.OriginalWidth;
        }

        const imageObj = new ImageExperienceImage(imageExperienceModelSettings);

        if (isImageFittingEnabled) {
          imageObj.setROIInfo(mtImageModel.ROIInfo, false, true);
        }

        return imageObj;
      });
    });
  }

  getImageListFromImages(mtImagesArr) {
    return _.map(mtImagesArr, (mtImage) => {
      return new ImageExperienceImage({
        id: mtImage.Asset.Id,
        src: stripOffCropParamsFromImageUrl(mtImage.Asset.ImageUrl),
        type: constants.subTypeToType[mtImage.Asset.SubType],
        cropSettings: {
          SourceX: mtImage.Asset.CropX,
          SourceY: mtImage.Asset.CropY,
          SourceHeight: mtImage.Asset.CropHeight,
          SourceWidth: mtImage.Asset.CropWidth,
        },
        associationType: mtImage.Asset.SubType,
      });
    });
  }

  fetchImageMetaDataAsync(imageList) {
    const imageIdsToFetch = _.unique(_.map(imageList, (imageObj) => {
      return imageObj.IdString;
    }));

    return imageRepository.getImagesByIdsAsync(imageIdsToFetch).then((returnedImages) => {
      const imageIdToMetaDataMap = _.reduce(returnedImages, (memo, returnedImage) => {
        const newData = {};

        newData[returnedImage.Id] = returnedImage;
        return _.defaults(newData, memo);
      }, {});

      _.each(imageList, (imageObj) => {
        imageObj.Width = imageIdToMetaDataMap[imageObj.Id].Width;
        imageObj.Height = imageIdToMetaDataMap[imageObj.Id].Height;
      });
    });
  }
}

const dataModelUtil = new DataModelConverter();

export default dataModelUtil;
